import { useEffect, useRef, useState, type ReactNode } from 'react'
import { CheckmarkCircle16Filled, FolderOpen20Regular, ArrowRight20Regular } from '@fluentui/react-icons'
import { registerReveal } from '../lib/reveal'

const steps = [
  {
    id: 'observe',
    title: 'Observe',
    text: 'Inventory the directories, file types, ages, duplicates and effective permissions that already exist.',
  },
  {
    id: 'classify',
    title: 'Classify',
    text: 'Score each document against your business ontology using rules, embeddings and an optional bounded model pass.',
  },
  {
    id: 'simulate',
    title: 'Simulate',
    text: 'Produce the full destination plan — names, collisions, ACL impact and confidence — without writing anything.',
  },
  {
    id: 'approve',
    title: 'Approve',
    text: 'Execute only an approved, immutable plan, with a durable journal, verification and a rollback path.',
  },
]

/**
 * Two jobs, two mechanisms: the shared reveal controller plays the scene in (so a
 * scene scrolled past between frames still ends up visible), while a narrow band
 * across the middle of the viewport decides which rail step is current.
 */
function Scene({ id, children, onActive }: { id: string; children: ReactNode; onActive: (id: string) => void }) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const unregister = registerReveal(node)

    if (typeof IntersectionObserver === 'undefined') {
      onActive(id)
      return unregister
    }

    const focus = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) onActive(id)
      },
      { threshold: 0, rootMargin: '-42% 0px -42% 0px' },
    )

    focus.observe(node)
    return () => {
      unregister()
      focus.disconnect()
    }
  }, [id, onActive])

  return (
    <div className="gov-scene" ref={ref} id={`gov-${id}`}>
      {children}
    </div>
  )
}

const observedRows = [
  ['\\\\office-fs\\Shared\\Documents', '12,480'],
  ['\\\\office-fs\\Shared\\Old contracts', '2,140'],
  ['\\\\office-fs\\Shared\\Miscellaneous', '6,904'],
  ['\\\\office-fs\\Shared\\Σαρώσεις', '1,318'],
]

const classified = [
  ['Contract', 96],
  ['Invoice', 94],
  ['Δελτίο αποστολής', 88],
  ['Needs review', 41],
]

const journal = [
  ['Plan 2026-07-12 · 231 operations', 'Approved'],
  ['Pre-flight ACL revalidation', 'Passed'],
  ['Execution journal written', 'Durable'],
  ['Checksum verification', '231 / 231'],
]

export function GovernanceFlow() {
  const [active, setActive] = useState('observe')

  return (
    <div className="gov">
      <aside className="gov-rail">
        <ol>
          {steps.map((step, index) => (
            <li key={step.id} className={active === step.id ? 'is-active' : ''}>
              <a href={`#gov-${step.id}`}>
                <span className="mono">0{index + 1}</span>
                <b>{step.title}</b>
                <small>{step.text}</small>
              </a>
            </li>
          ))}
        </ol>
        <p className="gov-guard">
          <CheckmarkCircle16Filled />
          <span>
            No stage before <b>Approve</b> is allowed to modify a file.
          </span>
        </p>
      </aside>

      <div className="gov-scenes">
        <Scene id="observe" onActive={setActive}>
          <div className="scene-frame ticked">
            <header className="scene-head mono">
              <span>Discovery</span>
              <small>read-only</small>
            </header>
            <ul className="scan-tree">
              {observedRows.map(([path, count], index) => (
                <li key={path} style={{ ['--i' as string]: index }}>
                  <FolderOpen20Regular />
                  <span className="mono">{path}</span>
                  <b className="mono">{count}</b>
                </li>
              ))}
              <i className="scan-beam" />
            </ul>
            <footer className="scene-stats mono">
              <span>
                <b>22,842</b> files
              </span>
              <span>
                <b>1,904</b> folders
              </span>
              <span>
                <b>14</b> types
              </span>
              <span>
                <b>EN · EL</b>
              </span>
            </footer>
          </div>
        </Scene>

        <Scene id="classify" onActive={setActive}>
          <div className="scene-frame ticked">
            <header className="scene-head mono">
              <span>Classification</span>
              <small>evidence retained</small>
            </header>
            <ul className="score-list">
              {classified.map(([label, score], index) => (
                <li key={label as string} style={{ ['--i' as string]: index, ['--w' as string]: `${score}%` }}>
                  <span>{label}</span>
                  <i>
                    <u className={(score as number) < 60 ? 'is-low' : undefined} />
                  </i>
                  <b className="mono">{score}%</b>
                </li>
              ))}
            </ul>
            <p className="scene-note">
              Deterministic rules run first. Embeddings and an optional bounded model pass only add evidence — never a
              silent decision.
            </p>
          </div>
        </Scene>

        <Scene id="simulate" onActive={setActive}>
          <div className="scene-frame ticked">
            <header className="scene-head mono">
              <span>Dry run</span>
              <small>nothing written</small>
            </header>
            <div className="diff">
              <div className="diff-side">
                <span className="mono diff-label">Current</span>
                <p className="mono">Shared\Miscellaneous\</p>
                <p className="mono is-file">scan_0142.pdf</p>
              </div>
              <div className="diff-arrow">
                <ArrowRight20Regular />
              </div>
              <div className="diff-side is-proposed">
                <span className="mono diff-label">Proposed</span>
                <p className="mono">Clients\Asteria Group\Invoices\</p>
                <p className="mono is-file">2026-184 — Asteria — Invoice.pdf</p>
              </div>
            </div>
            <div className="scene-badges">
              <span className="tag tag-ok">
                <CheckmarkCircle16Filled />
                ACL impact: none
              </span>
              <span className="tag tag-warn">2 name collisions resolved</span>
              <span className="tag">231 operations planned</span>
            </div>
          </div>
        </Scene>

        <Scene id="approve" onActive={setActive}>
          <div className="scene-frame ticked">
            <header className="scene-head mono">
              <span>Execution record</span>
              <small>immutable</small>
            </header>
            <ul className="journal">
              {journal.map(([label, state], index) => (
                <li key={label} style={{ ['--i' as string]: index }}>
                  <CheckmarkCircle16Filled />
                  <span>{label}</span>
                  <b className="mono">{state}</b>
                </li>
              ))}
            </ul>
            <div className="scene-badges">
              <span className="tag tag-brand">Rollback available</span>
              <span className="tag">Endpoint file actions remain disabled</span>
            </div>
          </div>
        </Scene>
      </div>
    </div>
  )
}
